import React from 'react';
import videojs from 'video.js';
import vttThumbnails from 'videojs-vtt-thumbnails/src/plugin.js';
import SubscriptionModal from '../generalComponents/SubscriptionModal';
import { UserContext } from '../../context/index.js';

//utils
import callApi from '../../utils/callApi';

//constants
import {
    FEATURED_MAIN_PROGRAM,
    FEATURED_MAIN_PROGRAM_GUEST,
    FETCH_VIDEO_SEGMENT_BY_ID,
    FETCH_VIDEO_PROGRAM_BY_ID,
    FETCH_VIDEO_VINTAGE_BY_ID,
    FETCH_VIDEO_AUDITION_BY_ID,
    TRACK_SEGMENT_VIDEO_WATCH
} from '../../constants/apiUrls/apiUrls';

//components
import PlayerInfo from './PlayerInfo';
import '../../css/player.css';
import RelatedSegmentsListVertical from './RelatedSegmentsVertical';
import CastVideoPlayer from './CastVideoPlayer';
import './CastVideoPlayer.css';

const DEFAULT_VIDEO_RESOLUTION = "SIZE_720";


class VideoPlayer extends React.Component {
    constructor(props) {
        super(props);
        const { user } = props;
        const useHLSAbr = (user && user.features && (user.features.indexOf("HLS_ABR") >= 0));

        this.state = {
            program: undefined,
            programSegments: undefined,
            currentPosition: undefined,
            isLoggedIn: false,
            showSubscriptionModal: false,
            resolution: useHLSAbr ? 'Auto' : DEFAULT_VIDEO_RESOLUTION,
            defaultClip: null
        };

        this.seekTimeAtFailed = null;
        this.intervalId = null;
        this.monitorId = null;
        this.lastPlayed = null;
    }

    monitor = () => {
        const now = new Date().getTime();
        this.sendWatchTracking(this.state.currentPosition, now - this.lastPlayed);
        this.lastPlayed = now;
    };

    playEvent = () => {
        this.lastPlayed = new Date().getTime();
        this.monitorId = setInterval(this.monitor, 10000)
    };

    pauseEvent = () => {
        this.pause(this.state.currentPosition);
    };

    pause = (position) => {
        if (this.lastPlayed !== null) {
            const now = new Date().getTime();
            this.sendWatchTracking(position, now - this.lastPlayed);
            clearInterval(this.monitorId);
            this.lastPlayed = null;
        }
    };

    sendWatchTracking = async (position, msecs) => {
        if (msecs > 0) {
            const { programSegments } = this.state;
            const programSegmentId = programSegments[position].programSegmentId;
            if (programSegmentId) {
                const url = TRACK_SEGMENT_VIDEO_WATCH
                    .replace('{programSegmentId}', programSegmentId);
              const data = {
                duration: ~~(this.player.duration() * 1000),
                msecs: msecs
              };
              callApi(url, 'PATCH', false, data, true, true)
                    .catch(err => console.log(err));
            }
        }
    };

    openSubscriptionModal = () => {
        this.setState({
            showSubscriptionModal: true
        });
    };

    closeSubscriptionModal = () => {
        this.setState({
            showSubscriptionModal: false
        });
    };

    playButton = () => {
        if (this.player) {
            this.player.play();
        }
    };

    applyResolution = (resolutionSize) => {
      const { program, currentPosition } = this.state;

      const clip = program.programSegments[currentPosition].clip;
      const currentTime = this.player.currentTime();
      const srcUrl = this.getVideoSrc(clip, resolutionSize);

      this.setState({resolution: resolutionSize});
      this.player.poster(null);
      this.player.src(srcUrl);
      this.player.play().then(() => {
        this.player.currentTime(currentTime);
      });
    }

    changeVideoSrc = (programSegment, position) => {
        const { currentPosition, program, resolution } = this.state;
        const { user } = this.props;
        if(!user.authenticated && position !== 0){
            this.openSubscriptionModal()
        }
        else {
        if (typeof programSegment === 'undefined') return false;
        const error = this.player.error();
        if (currentPosition !== position) {
          this.seekTimeAtFailed = null;
        }
        if (currentPosition !== position || error) {
            this.setState({ currentPosition: position }, () => {
              this.pause(currentPosition);
              this.player.src(this.getVideoSrc(programSegment.clip, resolution));
              //remove next segment poster
              this.player.poster(null);
              // reset download items
              if (program.programSegments) {
                const DownloadMenu = this.player.getChild('controlBar').getChild('DownloadMenu');
                // this is required to keep download button from vintage videos ;(
                if (DownloadMenu) {
                  this.player.getChild('controlBar').removeChild('DownloadMenu');
                  this.player.getChild('controlBar').addChild(
                    'DownloadMenu',
                    {
                      items: this.getProgramItems()
                    },
                    this.player.controlBar.children_.length
                  );
                }
              }
              this.player.play();
              this.setVTT(programSegment.clip);
            });
        }
    }
    };

    createDownloadButton = () => {
        const MenuItem = videojs.getComponent('MenuItem');
        const DownloadItem = videojs.extend(MenuItem, {
            constructor: function(player, options) {
                this.level = options.label;
                this.href = options.href;
                MenuItem.call(this, player, options);
                if (!this.href) {
                    videojs.dom.addClass(this.el(), 'vjs-download-title');
                }
                this.controlText(this.level);
            }
        });
        DownloadItem.prototype.handleClick = function(event) {
            MenuItem.prototype.handleClick.call(this, event);
            if (this.href) {
                window.open(this.href, '_blank');
            }
        };
        MenuItem.registerComponent('DownloadItem', DownloadItem);
        const MenuButton = videojs.getComponent('MenuButton');
        const DownloadMenu = videojs.extend(MenuButton, {
            constructor: function(player, options) {
                this.items = options.items;
                MenuButton.call(this, player, options);
                this.controlText('Download');
                this.el().setAttribute('aria-label', 'Download Menu');
                videojs.dom.addClass(this.el(), 'vjs-download-button');
            },
            createItems: function() {
                var i, menuItems, ref, value;
                menuItems = [];
                ref = this.items;
                for (i in ref) {
                    value = ref[i];
                    menuItems.push(new DownloadItem(this.player_, value));
                }
                return menuItems;
            },
            dispose: function() {
                return this.parentNode && this.parentNode.removeChild(this);
            }
        });
        MenuButton.registerComponent('DownloadMenu', DownloadMenu);
    };

    createPlayNextButtonForVideoJs = () => {
        let self = this;
        let Button = videojs.getComponent('Button');
        let nextButton = videojs.extend(Button, {
            constructor: function() {
                Button.apply(this, arguments);
                this.addClass('icon-angle-right');
            },
            handleClick: function() {
                self.changeVideoSrc(
                    self.state.programSegments[
                        self.state.currentPosition + 1
                    ],
                    self.state.currentPosition + 1
                );
            }
        });

        videojs.registerComponent('nextButton', nextButton);
    };

    createResolutionMenu = () => {
      const self = this;
      const MenuItem = videojs.getComponent('MenuItem');
      const ResolutionItem = videojs.extend(MenuItem, {
          constructor: function(player, options) {
              this.level = options.label;
              MenuItem.call(this, player, options);
              this.controlText(this.level);
              if (options.size === self.state.resolution) {
                videojs.dom.addClass(this.el(), 'vjs-selected');
              }
          }
      });
      ResolutionItem.prototype.handleClick = function(event) {
          MenuItem.prototype.handleClick.call(this, event);
          const options = this.options();
          const siblings = this.el().parentNode.children;

          if (options.resolution === 'Auto'){
              self.applyResolution(DEFAULT_VIDEO_RESOLUTION)
          }
          else {self.applyResolution(options.size)};
          for (let i = 0; i < siblings.length; i++) {
            videojs.dom.removeClass(siblings[i], 'vjs-selected');
          }
          videojs.dom.addClass(this.el(), 'vjs-selected');
      };
      MenuItem.registerComponent('ResolutionItem', ResolutionItem);
      const MenuButton = videojs.getComponent('MenuButton');
      const ResolutionMenu = videojs.extend(MenuButton, {
          constructor: function(player, options) {
              this.items = options.items;
              MenuButton.call(this, player, options);
              this.controlText('Download');
              this.el().setAttribute('aria-label', 'Resolution Menu');
              videojs.dom.addClass(this.el(), 'vjs-icon-hd');
          },
          createItems: function() {
              var i, menuItems, ref, value;
              menuItems = [];
              ref = this.items;
              for (i in ref) {
                  value = ref[i];
                  menuItems.push(new ResolutionItem(this.player_, value));
              }
              return menuItems;
          },
          dispose: function() {
              return this.parentNode && this.parentNode.removeChild(this);
          }
      });
      MenuButton.registerComponent('ResolutionMenu', ResolutionMenu);
    };

    createPlayPreviousButtonForVideoJs = () => {
        let self = this;
        let Button = videojs.getComponent('Button');
        let prevButton = videojs.extend(Button, {
            constructor: function() {
                Button.apply(this, arguments);
                this.addClass('icon-angle-left');
            },
            handleClick: function() {
                self.changeVideoSrc(
                    self.state.programSegments[
                        self.state.currentPosition - 1
                    ],
                    self.state.currentPosition - 1
                );
            }
        });

        videojs.registerComponent('prevButton', prevButton);
    };

    decideUrl = () => {
        const { videoElement, typeOfListing, user } = this.props;
        const isSubscriptionExpired = user.authenticated && !user.user.activeSubscription;
        if (typeof videoElement !== 'undefined') {
            switch (typeOfListing) {
                case 'all':
                case 'date':
                    if (videoElement.programId) {
                        return FETCH_VIDEO_PROGRAM_BY_ID.replace(
                            '{id}',
                            videoElement.programId
                        );
                    }
                    return FETCH_VIDEO_SEGMENT_BY_ID.replace(
                        '{id}',
                        videoElement.programSegmentId
                    );
                case 'audition':
                    return FETCH_VIDEO_AUDITION_BY_ID.replace(
                        '{id}',
                        videoElement.auditionId
                    );
                case 'vintage':
                    return FETCH_VIDEO_VINTAGE_BY_ID.replace(
                        '{id}',
                        videoElement.id
                    );
                default:
                    return FETCH_VIDEO_SEGMENT_BY_ID.replace(
                        '{id}',
                        videoElement.programSegmentId
                    );
            }
        }
        if ((user.authenticated && (!isSubscriptionExpired || !!user.user.account.anchor)))  return FEATURED_MAIN_PROGRAM;
        return FEATURED_MAIN_PROGRAM_GUEST;
    };

    getProgramSegment = (programSegment) => {
        return programSegment.programSegmentId ?
            {
                programSegmentId: programSegment.programSegmentId,
                segment: programSegment.segment,
                description: programSegment.description,
                createdAt: programSegment.createdAt,
                clip: programSegment.clip
            }
            :
            {
                programSegmentId: undefined,
                segment: { name: '' },
                description: '',
                createdAt: undefined,
                clip: undefined
            }
            ;
    }

    fetchMainProgram = url => {
        const { searchAnchorId, searchSegmentId } = this.props;
        callApi(url, 'get', {}).then(res => {
            let currentPosition = 0;
            const program = res.data;
            this.setState((state) => { return {defaultClip: res.data.clip.defaultClipUrl}})
            const programSegments = (program.programSegments ? program.programSegments : [program]).map(this.getProgramSegment);
            if (searchAnchorId) {
                currentPosition = program.programSegments
                    ? programSegments.reduce((acc, seg, ind) => {
                          if (seg.anchors && seg.anchors.length) {
                              seg.anchors.forEach(anc => {
                                  if (anc.id == searchAnchorId) acc = ind;
                              });
                          }
                          return acc;
                      }, 0)
                    : 0;
            }
            if (searchSegmentId) {
                currentPosition = program.programSegments
                    ? programSegments.reduce((acc, seg, ind) => {
                          if (seg.segment.id == searchSegmentId) acc = ind;
                          return acc;
                      }, 0)
                    : 0;
            }
            
            this.setState({ program, programSegments, currentPosition }, () =>
                this.initVideoJs()
            );
        });
    };

    setVTT = clip => {
        if (this.player.vttThumbnails && clip && clip.vtt) {
            if (typeof this.player.vttThumbnails === 'function') {
                // first use
                this.player.vttThumbnails({
                    src: clip.vtt.SRC,
                    sprite: clip.vtt.SPRITE
                });
            } else {
                // update src
                this.player.vttThumbnails.src(clip.vtt.SRC, clip.vtt.SPRITE);
            }
            const img1 = document.createElement('img');
            img1.src = clip.vtt.SPRITE; // Assigning the img src immediately requests the image
        }
    };
    getProgramItems = () => {
        const { program, currentPosition, programSegments } = this.state;
        const segments = program.programSegments || programSegments;
        const { downloadClipUrls } = segments[currentPosition].clip;
        let items = [{ label: 'Program' }];

        if (program.fullVideoUrls && program.isHd) {
            program.fullVideoUrls.SIZE_1080 &&
                items.push({ label: '1080p', href: program.fullVideoUrls.SIZE_1080 });
            program.fullVideoUrls.SIZE_720 &&
                items.push({ label: '720p', href: program.fullVideoUrls.SIZE_720 });
            program.fullVideoUrls.SIZE_480 &&
                items.push({ label: '480p', href: program.fullVideoUrls.SIZE_480 });
            program.fullVideoUrls.SIZE_360 &&
                items.push({ label: '360p', href: program.fullVideoUrls.SIZE_360 });
        }

        if (downloadClipUrls) {
            items.push({ label: 'Segment' });
            downloadClipUrls.SIZE_1080 &&
                items.push({
                    label: '1080p',
                    href: downloadClipUrls.SIZE_1080
                });
            downloadClipUrls.SIZE_720 &&
                items.push({ label: '720p', href: downloadClipUrls.SIZE_720 });
            downloadClipUrls.SIZE_480 &&
                items.push({ label: '480p', href: downloadClipUrls.SIZE_480 });
            downloadClipUrls.SIZE_360 &&
                items.push({ label: '360p', href: downloadClipUrls.SIZE_360 });
        }
        return items;
    };

    getSegmentItems = () => {
        const { program } = this.state;
        const { downloadClipUrls } = program.clip;
        let items = [];

        downloadClipUrls.SIZE_1080 &&
            items.push({ label: '1080p', href: downloadClipUrls.SIZE_1080 });
        downloadClipUrls.SIZE_720 &&
            items.push({ label: '720p', href: downloadClipUrls.SIZE_720 });
        downloadClipUrls.SIZE_480 &&
            items.push({ label: '480p', href: downloadClipUrls.SIZE_480 });
        downloadClipUrls.SIZE_360 &&
            items.push({ label: '360p', href: downloadClipUrls.SIZE_360 });

        return items;
    };

    getVideoResolutions = () => {
      const { program, programSegments, currentPosition } = this.state;
      const standardUrls = Object.values(program.clip).every((v) => v !== null) ? program.clip.clipUrls : programSegments[currentPosition].clip.clipUrls;
      const items = Object.keys(standardUrls).map(size => {
        const resolutionBase = size.replace('SIZE_', '');
        return {
          size: size,
          resolution: resolutionBase,
          label: resolutionBase + 'p'
        }
      }).sort((a, b) => b.resolution - a.resolution);
      items.push({ resolution: 'Auto', label: 'Auto', size: 'Auto' });
      return items;
    }

    getVideoSrc = (clip, resolution) => {
        const { user } = this.props;

        resolution = resolution || DEFAULT_VIDEO_RESOLUTION;
        const useHLS = (user && user.features && (user.features.indexOf("HLS") >= 0));
        const useHLSOverCdn = (user && user.features && (user.features.indexOf("HLS_OVER_CDN") >= 0));
        const useHLSAbr = (user && user.features && (user.features.indexOf("HLS_ABR") >= 0));

        let srcUrl;
        if ((!user || !user.authenticated) && this.props.homePage){
            srcUrl = this.state.defaultClip
            
        }
        else {
        if (resolution === 'Auto' && clip.abrClipUrl) {
          console.log("Using HLS ABR");
          srcUrl = clip.abrClipUrl;
        } else if (useHLSOverCdn && clip.clipHlsCdnUrls) {
            console.log("Using HLS over CDN with resolution: " + resolution);
            srcUrl = clip.clipHlsCdnUrls[resolution];
        } else if (useHLS) {
            console.log("Using HLS with resolution: " + resolution);
            srcUrl = clip.clipHlsUrls ? clip.clipHlsUrls[resolution] : (clip.defaultClipUrl);
        } else {
          // in case of older videos not having abr stream
          if (useHLSAbr && !clip.abrClipUrl) {
            srcUrl = clip.clipUrls[DEFAULT_VIDEO_RESOLUTION];
            this.setState({resolution: DEFAULT_VIDEO_RESOLUTION});
          } else {
            srcUrl = clip.clipUrls ? clip.clipUrls[resolution] : (clip.defaultClipUrl);
          }
        }
        }
        return srcUrl;
    };

    hasHLSSources = (clip => {
      const { user } = this.props;
      const useHLS = (user && user.features && (user.features.indexOf("HLS") >= 0));
      const useHLSOverCdn = (user && user.features && (user.features.indexOf("HLS_OVER_CDN") >= 0));
      const hasHLSActive = useHLS || useHLSOverCdn;
      const hasHLSURLS = clip.clipHlsUrls || clip.clipHlsCdnUrls;
      const res = Boolean(hasHLSActive && hasHLSURLS);
      return res;
    })

    initVideoJs = () => {
        const { program, programSegments, resolution } = this.state;
        const { user } = this.props;
        const useHLS = (user && user.features && (user.features.indexOf("HLS") >= 0));
        const useHLSOverCdn = (user && user.features && (user.features.indexOf("HLS_OVER_CDN") >= 0));
        const useHLSAbr = (user && user.features && (user.features.indexOf("HLS_ABR") >= 0));
        const userCanDownload = user?.user?.account?.hasDownloadMenu;
        if (program === undefined) {
            this.player && this.player.src('');
            return;
        }
        videojs.registerPlugin('vttThumbnails', vttThumbnails);
        if (userCanDownload && (user.user.activeSubscription || user.user.account.anchor)) {
            this.createDownloadButton();
        }

        const self = this;
        //if it has segments then  its a PROGRAM , if not its a SEGMENT
        if (program.programSegments) {
            this.createPlayNextButtonForVideoJs();
            this.createPlayPreviousButtonForVideoJs();
            if ((useHLSAbr || useHLS || useHLSOverCdn) && self.hasHLSSources(programSegments[this.state.currentPosition].clip)) {
              this.createResolutionMenu();
            }
            // instantiate Video.js
            this.player = videojs(
                this.videoNode,
                this.props,
                function onPlayerReady() {

                    //Removing default volume panel and adding the vertical volume panel
                    this.getChild("controlBar").removeChild('volumePanel');
                    this.getChild("controlBar").addChild('volumePanel', {inline: false, vertical: true}, 1);
                                        
                    //Adding the left and right buttons in the player element to position it perfectly on mobile
                    this.$(".vjs-tech", this.addChild('nextButton', { text: "Next", inactivityTimeout: 5000}, 1));
                    this.$(".vjs-tech", this.addChild('prevButton', { text: "Previous", inactivityTimeout: 5000}, 1));
                    
                    this.getChild('controlBar').addChild(
                        'nextButton',
                        {
                            text: 'Next'
                        },
                        1
                    );
                    this.getChild('controlBar').addChild(
                        'prevButton',
                        {
                            text: 'Previous'
                        },
                        1
                    );
                    if ((useHLSAbr || useHLS || useHLSOverCdn) && self.hasHLSSources(programSegments[self.state.currentPosition].clip)) {
                      this.getChild('controlBar').addChild(
                          'ResolutionMenu',
                          {
                              items: self.getVideoResolutions()
                          },
                          this.controlBar.children_.length
                      );
                    }
                    if (userCanDownload) {
                        this.getChild('controlBar').addChild(
                            'DownloadMenu',
                            {
                                items: self.getProgramItems()
                            },
                            this.controlBar.children_.length
                        );
                    }
                    this.src(self.getVideoSrc(programSegments[self.state.currentPosition].clip), resolution);
                    if (self.props.mutedVideo) {
                        const videoEl = document.getElementsByTagName('video')[0];
                        videoEl.muted = true;
                        this.volume(0);
                    }
                },
            );
            this.setVTT(programSegments[this.state.currentPosition].clip);

            this.player.on('error', () => {
              const mediaError = this.player.error();
              const time = this.player.currentTime();
              this.player.errorDisplay.close();
              if (mediaError.code === 2) {
                this.seekTimeAtFailed = time;
                if (!this.intervalId) {
                    this.intervalId = setInterval(this.playerReload, 1000);
                  }
              }
              if (mediaError.code === 3) {
                this.seekTimeAtFailed = time;
                if (!this.intervalId) {
                  this.intervalId = setInterval(this.playerReload, 5000);
                }
              }
              if (mediaError.code === 4) {
                this.seekTimeAtFailed = time;
                if (!this.intervalId) {
                    this.intervalId = setInterval(this.playerReload, 5000);
                  }
              }
            })
            // remove next segment poster
            // this.player.on('loadstart', () => {
            //   this.player.poster(this.getPoster())
            // })
            this.player.on('ended', () => {
              this.player.poster(null);
              const error = this.player.error();
              if (error) {
                this.changeVideoSrc(programSegments[self.state.currentPosition], self.state.currentPosition);
              } else {
                this.changeVideoSrc(programSegments[self.state.currentPosition + 1], self.state.currentPosition + 1);
              }
            });
        } else {
            if (useHLSAbr && self.hasHLSSources(program.clip)) {
              this.createResolutionMenu();
            }
            this.player = videojs(
                this.videoNode,
                this.props,
                function onPlayerReady() {
                    if (program.clip.downloadClipUrls && user.authenticated && userCanDownload) {
                        if (useHLSAbr && self.hasHLSSources(program.clip)) {
                          this.getChild('controlBar').addChild(
                            'ResolutionMenu',
                            {
                                items: self.getVideoResolutions()
                            },
                            this.controlBar.children_.length
                          );
                        }
                        if (userCanDownload) {
                            this.getChild('controlBar').addChild(
                                'DownloadMenu',
                                {
                                    items: self.getProgramItems()
                                },
                                this.controlBar.children_.length
                            );
                        }
                    }
                    this.src(self.getVideoSrc(program.clip, resolution));
                    if (self.props.mutedVideo) {
                        const videoEl = document.getElementsByTagName('video')[0];
                        videoEl.muted = true;
                        this.volume(0);
                    }
                }
            );
            this.player.on('ended', () => {
                if (user.authenticated === false) {
                    this.setState({showSubscriptionModal: true});
                  }
            })
            this.setVTT(program.clip);
        }

        this.player.on("pause", this.pauseEvent);
        this.player.on("play", this.playEvent);

        this.player.poster(this.getPoster());
    };

    playerReload = () => {
      const error = this.player.error();
      if (error) {
        // this.changeVideoSrc(
        //   this.state.program.programSegments[
        //     this.state.currentPosition
        //   ],
        //   this.state.currentPosition
        // );
        this.player.currentTime(this.seekTimeAtFailed);
        this.player.play();
      } else {
        this.clearReconnectInterval();
      }
    }

    componentDidMount() {
        let url = this.decideUrl();
        this.fetchMainProgram(url);
    }

    // destroy player on unmount
    componentWillUnmount() {
        this.pauseEvent();
        if (this.player) {
            this.player.dispose();
        }
        this.clearReconnectInterval();
    }

    clearReconnectInterval = () => {
        clearInterval(this.intervalId)
        this.intervalId = null;
    }

    decidePlayerInfo = () => {
        const { program, programSegments, currentPosition } = this.state;
        const {
            isProgram,
            user,
            forceHideNextSegmentButton,
            hidePlayerInfo
        } = this.props;

        if (isProgram && !hidePlayerInfo) {
            return (
                <UserContext.Consumer>
                    {value => (
                        <PlayerInfo
                            forceHideNextSegmentButton={forceHideNextSegmentButton}
                            isSubscribed={
                                value.user && value.user.activeSubscription
                            }
                            user={user}
                            currentPosition={currentPosition}
                            programSegments={programSegments}
                            changeVideoSrc={this.changeVideoSrc}
                            program={program}
                            clearReconnectInterval={this.clearReconnectInterval}
                        />
                    )}
                </UserContext.Consumer>
            );
        }

        return false;
    };

    getPoster = (targetPosition) => {
      const { program, currentPosition } = this.state;
      if (targetPosition === null) return null;

      const position = targetPosition || currentPosition;
      const poster = program.programSegments
          ? program.programSegments[position].clip.imageUrl
          : (program.nextImageUrl || program.clip.imageUrl);

      return poster;
    };

    setVideoIsCasting = (boolean) => {
        this.setState({videoIsBeingCasted: boolean});
        this.player.pause();
    }

    render() {
        const { program, showSubscriptionModal, programSegments, videoIsBeingCasted } = this.state;
        const {
            customBcgColor,
            autoPlayVideo,
            loopVideo,
            mutedVideo,
            user
        } = this.props;

        if (program === undefined) {
            return false;
        }
        let videoClass = "video-js vjs-sk vjs-default-skin vjs-paused vp_18_video-dimensions vjs-fluid vjs-controls-enabled vjs-workinghover vjs-v6 vjs-user-active"
        return (

            <div
                className="masthead"
                style={{
                    backgroundColor: customBcgColor ? customBcgColor : '#252525'
                }}>
                <div className="container">
                    <SubscriptionModal
                        show={showSubscriptionModal}
                        closeModal={this.closeSubscriptionModal}
                    />
                    <h3 className='now-playing-title'>NOW PLAYING</h3>
                    
                    <div
                        id="video-player-container"
                        className={`clearfix video-player-container preview ${'video-player-container-mobile'}`}
                    >
                        <div className="full-player">
                            <div className="player-zone">
                                <div className="video-player">
                                    <div id="vp_18" className={`${videoIsBeingCasted && "cast-is-active"} mobile-video-wrapper ${programSegments.length === 1 ? 'sk-video-player-without-segment' : ''}`}>
                                        <div
                                            id="vp_18_wrapper"
                                            className={`sk-video-player ${programSegments.programSegmentId === undefined ? 'sk-video-player-full' : ''}`}
                                        >
                                            
                                            <video
                                                autoPlay={autoPlayVideo}
                                                loop={loopVideo}
                                                muted={mutedVideo}
                                                controls
                                                ref={node =>
                                                    (this.videoNode = node)
                                                }
                                                id="vp_18_video_html5_api"
                                                className={programSegments.length === 1 ? videoClass+= " full-width" : videoClass+=""}
                                                preload="none"
                                                playinline=""
                                                tabIndex="-1"
                                                type="application/x-mpegURL"
                                                playsInline
                                            />
                                            {window.chrome && <CastVideoPlayer segments={programSegments} user={user} getVideoSrc={this.getVideoSrc} setVideoIsCasting={this.setVideoIsCasting}/>}
                                        </div>
                                        {programSegments.length === 1 ? null : 
                                        <RelatedSegmentsListVertical
                                            programSegments={programSegments}
                                            changeVideoSrc={this.changeVideoSrc}
                                            currentPosition={this.state.currentPosition}
                                            program={this.state.program}
                                            openSubscriptionModal={this.openSubscriptionModal}
                                            isPromoActive={this.props.isPromoActive}
                                        />
                                        }
                                        {this.decidePlayerInfo()}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

VideoPlayer.contextType = UserContext;

export default VideoPlayer;
